/**
 * Mentions in an item's comments, outside React: who may be mentioned, the
 * "@" being typed at the caret, the "@Name" a pick puts in the text, which
 * picked people a comment still mentions when it is sent, and a comment's
 * text cut into plain text and mentions to show.
 */

import { matchesPerson, type NamedPerson, normalEmail, personLabel } from "@/lib/people";
import { isAdminRole } from "@/lib/roles";

/** A letter, a digit or an underscore: a mention never starts or ends next to one. */
const WORD = /[\p{L}\p{N}_]/u;

/** The longest text after an "@" the list follows: longer than any name, so a stray "@" lets go. */
const QUERY_MAX = 40;

/** How many people the list offers at most. */
export const MENTION_OPTIONS_MAX = 6;

/**
 * The people a comment can mention: the dashboard's accounts that reach the
 * roadmap (an admin, an owner or a developer), the viewer left out.
 */
export function mentionCandidates<T extends NamedPerson & { role: string }>(
  people: readonly T[],
  viewer: string,
): T[] {
  const me = normalEmail(viewer);
  return people.filter((person) => isAdminRole(person.role) && normalEmail(person.email) !== me);
}

/** How a mention reads in a comment: "@An Peeters", or "@an@example.com" for someone without a name. */
export function mentionLabel(person: NamedPerson): string {
  return `@${personLabel(person)}`;
}

/** An "@" being typed: where it stands in the text, and what follows it up to the caret. */
export interface MentionQuery {
  start: number;
  query: string;
}

/**
 * The mention being typed at the caret: the last "@" before it, at the start
 * of the text or after a space or line break, with no line break between it
 * and the caret. Null for none, such as the "@" inside an email address.
 */
export function mentionQueryAt(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start < 0 || (start > 0 && !/\s/.test(before.charAt(start - 1)))) {
    return null;
  }
  const query = before.slice(start + 1);
  if (/[\r\n]/.test(query) || query.length > QUERY_MAX) {
    return null;
  }
  return { start, query };
}

/** The people the list offers while an "@" is typed: who matches the text after it, by name or email. */
export function mentionOptions<T extends NamedPerson>(people: readonly T[], query: string): T[] {
  return people.filter((person) => matchesPerson(person, query)).slice(0, MENTION_OPTIONS_MAX);
}

/**
 * The text after a pick from the list: the "@" and what was typed after it
 * become the person's mention and a space, and the caret goes after them.
 */
export function insertMention(
  text: string,
  at: MentionQuery,
  person: NamedPerson,
): { text: string; caret: number } {
  const label = mentionLabel(person);
  const rest = text.slice(at.start + 1 + at.query.length);
  const spaced = rest.startsWith(" ") ? rest : ` ${rest}`;
  return { text: text.slice(0, at.start) + label + spaced, caret: at.start + label.length + 1 };
}

/** Whether a mention stands at this place in a text, whole: no letter or digit right before or after it. */
function mentionAt(text: string, index: number, label: string): boolean {
  return (
    text.startsWith(label, index) &&
    !WORD.test(text.charAt(index - 1)) &&
    !WORD.test(text.charAt(index + label.length))
  );
}

function containsMention(text: string, label: string): boolean {
  for (let index = text.indexOf(label); index >= 0; index = text.indexOf(label, index + 1)) {
    if (mentionAt(text, index, label)) {
      return true;
    }
  }
  return false;
}

/** Someone picked from the list while writing, with the mention the pick put in the text. */
export interface PickedMention {
  email: string;
  label: string;
}

/**
 * The mentions a comment is sent with: every person picked from the list
 * whose mention is still whole in the text, once each, in the order they were
 * picked, by their email in lower case. A mention deleted or edited away
 * mentions nobody, and an "@Name" typed by hand is plain text.
 */
export function sentMentions(text: string, picked: readonly PickedMention[]): string[] {
  const sent: string[] = [];
  for (const { email, label } of picked) {
    const normal = normalEmail(email);
    if (!sent.includes(normal) && containsMention(text, label)) {
      sent.push(normal);
    }
  }
  return sent;
}

/** A piece of a comment's text: plain text, or the mention of someone it mentions. */
export type CommentPart = { kind: "text"; text: string } | { kind: "mention"; text: string };

/**
 * A comment's text cut into plain text and mentions, to show each mention of
 * someone the comment mentions (by their name now, or their email) marked.
 * The longest mention wins where two start alike ("@An" and "@An Peeters").
 */
export function commentParts(
  body: string,
  mentions: readonly string[],
  people: ReadonlyMap<string, NamedPerson>,
): CommentPart[] {
  const labels = [
    ...new Set(
      mentions.flatMap((email) => {
        const person = people.get(normalEmail(email)) ?? { email };
        return [mentionLabel(person), `@${normalEmail(email)}`];
      }),
    ),
  ].sort((a, b) => b.length - a.length);
  const parts: CommentPart[] = [];
  let plainFrom = 0;
  let index = labels.length > 0 ? body.indexOf("@") : -1;
  while (index >= 0) {
    const label = labels.find((candidate) => mentionAt(body, index, candidate));
    if (label) {
      if (index > plainFrom) {
        parts.push({ kind: "text", text: body.slice(plainFrom, index) });
      }
      parts.push({ kind: "mention", text: label });
      plainFrom = index + label.length;
    }
    index = body.indexOf("@", label ? plainFrom : index + 1);
  }
  if (plainFrom < body.length) {
    parts.push({ kind: "text", text: body.slice(plainFrom) });
  }
  return parts;
}
